import { Column, Row } from "@silevis/reactgrid";
import { useEffect, useState } from "react";
import { useLoadData } from "../../services/DataService";
import useDataverseService from "../../services/DataverseService";
import "../ButtonComponent/ButtonComponent.css";
import { useData } from "../Contexts/DataProvider";
import { useHistoryPeriod } from "../Contexts/HistoryPeriodProvider";
import { useSelectedEntity } from "../Contexts/SelectedEntityProvider";
import { useSelectedTab } from "../Contexts/SelectedTabProvider";
import Dropdown, { Option } from "../Dropdown/Dropdown";
import Loading from "../Loading/Loading";
import BaseTable from "./BaseTable";
import { PeriodResponse } from "./HistoryBudgetTable";
import "./HistoryTables.css";
import {
  columnWidth,
  historyFtePriorityColumns,
  isDigitsOnly,
  renameHeaderRow,
} from "./TableHelpers";

const rowHeight = 35;

const sortColumns = (columns: Column[]): Column[] => {
  const isPriority = (col: Column) =>
    historyFtePriorityColumns.includes(String(col.columnId));
  return [
    ...columns.filter(isPriority),
    ...columns
      .filter((c) => !isPriority(c))
      .sort((a, b) => {
        const aId = String(a.columnId);
        const bId = String(b.columnId);

        if (isDigitsOnly(aId) && isDigitsOnly(bId)) {
          return Number(aId) - Number(bId);
        }
        return aId.localeCompare(bId);
      }),
  ];
};

const getColumns = (headCount: any[]) => {
  const uniqueColumns = new Set<string>();
  headCount.forEach((item: any) => {
    Object.keys(item).forEach((key) => {
      if (
        key !== "new_historyfteid" &&
        key !== "@odata.etag" &&
        key !== "new_submitted" &&
        key !== "_ownerid_value"
      )
        uniqueColumns.add(key);
    });
  });
  let columnsArray: Column[] = [];
  historyFtePriorityColumns.forEach((columnId) => {
    columnsArray.push({
      columnId,
      width: columnWidth(columnId),
      resizable: true,
    });
    uniqueColumns.delete(columnId);
  });
  uniqueColumns.forEach((key) =>
    columnsArray.push({
      columnId: key,
      width: 90,
      resizable: true,
    })
  );
  return sortColumns(columnsArray);
};

const calculateSummaries = (rows: any, columns: Column[]) => {
  const summaries: Record<string, number> = {};
  rows.forEach((row: any) => {
    columns.forEach((col) => {
      const val = row[col.columnId];
      if (typeof val === "number" && !isNaN(val)) {
        summaries[col.columnId] = (summaries[col.columnId] || 0) + val;
      }
    });
  });
  return summaries;
};

const getSummaryRow = (summaries: any, columns: Column[]) => {
  const cells = columns.map((col) => ({
    type: "text" as const,
    text:
      col.columnId === "new_codecompany"
        ? "Summary"
        : summaries[col.columnId]?.toLocaleString() ?? "",
    groupId: col.columnId,
    readOnly: true,
  }));
  return { rowId: "summary", height: rowHeight, cells };
};

const buildRows = (
  headCount: any[],
  columns: Column[],
  selectedPeriod?: string
): Row[] => {
  const headerRow: any = renameHeaderRow(columns);
  const filteredHeadCount = selectedPeriod
    ? headCount.filter((row) => row.new_period === selectedPeriod)
    : headCount;
  const dataRows = filteredHeadCount.map((row, idx) => ({
    rowId: idx,
    height: rowHeight,
    cells: columns.map((col) => {
      const value = row[col.columnId];
      const readOnly = true;
      if (typeof value === "number") {
        return {
          type: "number",
          value,
          groupId: col.columnId,
          readOnly,
        };
      } else if (typeof value === "string") {
        return {
          type: "text",
          text: value,
          groupId: col.columnId,
          readOnly,
        };
      } else {
        return {
          type: "text",
          text: "",
          groupId: col.columnId,
          readOnly,
        };
      }
    }),
    id: row.new_headcountid,
  }));

  const summaries = calculateSummaries(filteredHeadCount, columns);
  const summaryRow = getSummaryRow(summaries, columns);

  return [headerRow, ...dataRows, summaryRow];
};

const HistoryFteTable = () => {
  const { data } = useData();

  const { selectedEntity } = useSelectedEntity();
  const { selectedTab } = useSelectedTab();
  const { fetchDistinctValues } = useDataverseService();
  const { historyPeriod, setHistoryPeriod } = useHistoryPeriod();
  const { loadData } = useLoadData();

  const [columns, setColumns] = useState<Column[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [periods, setPeriods] = useState<Option[]>([]);
  const [isPeriodsOpen, setIsPeriodsOpen] = useState<boolean>(false);
  const [selectedPeriod, setSelectedPeriod] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchDistinctValues(
      "new_historyftes",
      "new_period",
      undefined,
      selectedEntity?.new_codecompany,
      selectedTab
    ).then((r: PeriodResponse) =>
      setPeriods(
        r.value.map((obj) => ({
          label: obj.new_period,
          value: obj.new_period,
        }))
      )
    );
  }, [selectedEntity]);

  useEffect(() => {
    if (historyPeriod) {
      setLoading(true);
      loadData().finally(() => setLoading(false));
    }
  }, [historyPeriod]);

  useEffect(() => {
    if (data.value && data.value.length > 0) {
      const newCols = getColumns(data.value);
      setColumns(newCols);
      setRows(buildRows(data.value, newCols, selectedPeriod));
    } else {
      setColumns([]);
      setRows([]);
    }
  }, [data, selectedPeriod]);

  const handlePeriodSelect = (period: Option | Option[]) => {
    if (Array.isArray(period)) {
      setSelectedPeriod(period[0].value);
      setHistoryPeriod(period[0].value);
    } else {
      if (period.value === selectedPeriod) {
        setSelectedPeriod(undefined);
        setHistoryPeriod(undefined);
      } else {
        setSelectedPeriod(period.value);
        setHistoryPeriod(period.value);
      }
      setIsPeriodsOpen(false);
    }
  };

  return (
    <div className="historyTable">
      <Dropdown
        filterOptions={periods}
        onSelect={handlePeriodSelect}
        setOpen={setIsPeriodsOpen}
        open={isPeriodsOpen}
        multiselect={false}
        placeholder="Select period"
        className="buttonComponent"
      />
      {historyPeriod &&
        (loading ? (
          <Loading />
        ) : (
          <BaseTable
            columns={columns}
            setColumns={setColumns}
            rows={rows}
            stickyLeftColumns={2}
            onCellsChanged={() => {}}
            modifyCellValues={() => {}}
            historyTable
          />
        ))}
    </div>
  );
};

export default HistoryFteTable;
